import React, { useState, useEffect, useRef } from 'react'
import { SelectPicker } from 'rsuite'
import { IFText, IFSkeleton } from 'Components'
import { Colors } from 'Theme'
import { useTranslation } from 'react-i18next'
import Styles from './IFSelect.module.css'
import styled from 'styled-components'
import { isPropsMatch } from 'Utils/PropsMatch'

const StyledSelectPicker = styled(SelectPicker)`
  && {
    width: 100%;

    .rs-picker-toggle {
      background-color: transparent !important;
      color: ${Colors.text};
      width: 100%;
      border-radius: 4px !important;
    }
    .rs-picker-toggle-active {
      border-color: ${Colors.primary};
    }
  }
`

const IFSelect = ({
  data,
  loadMore,
  loading,
  onSearch,
  style,
  onChange,
  value,
  disabled,
  defaultValue,
  setError,
  size,
  placeholder,
  paginationOffset,
  cleanable = true,
  handleMenuClose = () => {},
}) => {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedValue, setSelectedValue] = useState(
    value || defaultValue || null,
  )
  const [isTouched, setIsTouched] = useState(false)
  const [formattedData, setFormattedData] = useState([])
  const [menuOpen, setMenuOpen] = useState(false)
  const pickerRef = useRef()

  useEffect(() => {
    const safeData = Array.isArray(data) ? data : []
    if (loading && searchQuery.length !== 0) {
      setFormattedData([
        { label: 'Skeleton', value: 'Skeleton' },
        { label: 'Skeleton', value: 'Skeleton' },
        { label: 'Skeleton', value: 'Skeleton' },
        { label: 'Skeleton', value: 'Skeleton' },
      ])
    } else if (loading && searchQuery.length === 0 && paginationOffset === 0) {
      setFormattedData([
        { label: 'Skeleton', value: 'Skeleton' },
        { label: 'Skeleton', value: 'Skeleton' },
        { label: 'Skeleton', value: 'Skeleton' },
        { label: 'Skeleton', value: 'Skeleton' },
      ])
    } else if (loading && searchQuery.length !== 0) {
      setFormattedData(safeData)
    } else if (paginationOffset !== null && searchQuery.length === 0) {
      setFormattedData([
        ...safeData,
        { label: 'Skeleton', value: 'Skeleton' },
        { label: 'Skeleton', value: 'Skeleton' },
      ])
    } else {
      setFormattedData(safeData)
    }
  }, [loading, searchQuery])

  const handleChange = (selected) => {
    if (selected === 'Skeleton') return
    setSelectedValue(selected)
    if (onChange) {
      onChange(selected)
    }
    if (selected) {
      setError?.('')
    } else {
      setError?.(t('IFSelect.Required'))
    }
  }

  const handleOpen = () => {
    setMenuOpen(true)
    setIsTouched(true)
  }

  const handleClose = () => {
    setMenuOpen(false)
    handleMenuClose()
    if (isTouched && !selectedValue) {
      setError?.(t('IFSelect.Required'))
    }
  }
  const handleSearch = (val) => {
    setSearchQuery(val)

    if (val === '' && pickerRef.current) {
      setTimeout(() => {
        const menu = document.querySelector('.rs-picker-menu')
        if (menu) {
          const activeItem = menu.querySelector(
            '.rs-picker-select-menu-item-active',
          )
          if (activeItem) {
            activeItem.classList.remove('rs-picker-select-menu-item-active')
          }
        }
      }, 0)
    }

    if (onSearch) {
      onSearch(val)
    }
  }

  useEffect(() => {
    const handleScroll = (e) => {
      const menuEl = document.querySelector('.rs-picker-menu')
      if (menuEl && menuEl.contains(e.target)) return

      if (menuOpen && pickerRef?.current?.close) {
        pickerRef.current.close()
        const wrapper = pickerRef.current?.root
        const toggle = wrapper?.querySelector('.rs-picker-toggle')
        if (toggle) {
          toggle.blur()
          toggle.classList.remove('rs-picker-toggle-active')
        }
        setMenuOpen(false)
      }
    }
    window.addEventListener('scroll', handleScroll, true)
    return () => {
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [menuOpen])

  useEffect(() => {
    let observer
    let activeEl

    const disableKeys = (e) => {
      const keysToDisable = ['ArrowDown', 'ArrowUp', 'Enter']
      if (keysToDisable.includes(e.key)) {
        e.preventDefault()
        e.stopPropagation()
      }
    }
    if (menuOpen) {
      const attachListener = () => {
        activeEl = document.activeElement
        if (activeEl) {
          activeEl.addEventListener('keydown', disableKeys)
        }
      }
      const timer = setTimeout(() => {
        attachListener()
      }, 0)
      observer = new MutationObserver(() => {
        if (document.activeElement !== activeEl) {
          if (activeEl) {
            activeEl.removeEventListener('keydown', disableKeys)
          }
          attachListener()
        }
      })

      observer.observe(document.body, {
        subtree: true,
        childList: true,
      })

      return () => {
        clearTimeout(timer)
        if (activeEl) {
          activeEl.removeEventListener('keydown', disableKeys)
        }
        observer?.disconnect()
      }
    }
  }, [menuOpen])

  const onItemsRendered = (props) => {
    if (props.visibleStopIndex >= data.length - 1) {
      loadMore()
    }
  }
  const renderMenu = (menu) => (
    <div className={Styles.MenuContainer}>{menu}</div>
  )

  const renderMenuItem = (label, item) => (
    <div>
      {label === 'Skeleton' ? (
        <div className={Styles.Skeleton}>
          <IFSkeleton variant="text" />
        </div>
      ) : (
        <IFText className={Styles.MenuItem} style={{ color: Colors.text }}>
          {label}
        </IFText>
      )}
    </div>
  )

  return (
    <div className={Styles.WrapperDiv}>
      <StyledSelectPicker
        ref={pickerRef}
        onClean={() => {
          pickerRef?.current.close()
        }}
        container={() => document.body}
        preventOverflow={false}
        placement="autoVerticalStart"
        cleanable={cleanable}
        menuMaxHeight={250}
        menuAutoWidth
        size={size}
        data={formattedData}
        style={{ ...style }}
        virtualized
        onOpen={handleOpen}
        onClose={handleClose}
        renderMenu={renderMenu}
        disabledItemValues={['Skeleton']}
        renderMenuItem={renderMenuItem}
        listProps={{
          onItemsRendered,
        }}
        onSearch={handleSearch}
        searchBy={() => true}
        value={selectedValue}
        onChange={handleChange}
        placeholder={disabled ? defaultValue : placeholder}
        disabled={disabled}
        {...(!disabled && {
          renderValue: () => (
            <div>
              <IFText
                className={Styles.ValueStyle}
                style={{ color: Colors.text }}
              >
                {value}
              </IFText>
            </div>
          ),
        })}
      />
    </div>
  )
}

function shouldSkipRender(prevProps, nextProps) {
  return isPropsMatch(prevProps, nextProps, [
    'data',
    'loadMore',
    'loading',
    'onSearch',
    'style',
    'onChange',
    'value',
    'disabled',
    'defaultValue',
    'setError',
    'size',
    'placeholder',
    'paginationOffset',
    'cleanable',
  ])
}

export default React.memo(IFSelect, shouldSkipRender)
